import { createApp } from 'vue';
import { createPinia } from 'pinia';
import VueKonva from 'vue-konva';
import { createNotivue } from 'notivue';

import AppRoot from './AppRoot.vue';
import router, { resolveAuthedHomeRouteWithCatalog } from './router';
import '../shared/assets/tailwind.css';
import '@krafta/ui-kit/styles.css';
import { APP_NOTIFICATION_DURATION } from '@shared/notifications';
import { useAuthStore } from './stores/auth';
import { setupAxiosInterceptors } from './plugins/axios';
import { installStaleAssetRecovery } from './staleAssetRecovery';
import { useAttendanceLockStore } from '@features/attendance/attendanceLockStore';

installStaleAssetRecovery(window);

const pinia = createPinia();
const notivue = createNotivue({
  position: 'top-right',
  limit: 4,
  enqueue: true,
  avoidDuplicates: false,
  pauseOnHover: true,
  pauseOnTouch: true,
  pauseOnTabChange: true,
  animations: {
    enter: 'app-toast-enter',
    leave: 'app-toast-leave',
    clearAll: 'app-toast-clear-all'
  },
  transition: 'transform 260ms cubic-bezier(0, 0, 0.2, 1)',
  notifications: {
    success: { duration: APP_NOTIFICATION_DURATION.success },
    info: { duration: APP_NOTIFICATION_DURATION.info },
    warning: { duration: APP_NOTIFICATION_DURATION.warning },
    error: { duration: APP_NOTIFICATION_DURATION.error },
    'promise-resolve': { duration: APP_NOTIFICATION_DURATION.success },
    'promise-reject': { duration: APP_NOTIFICATION_DURATION.error }
  }
});

const app = createApp(AppRoot);
app.use(pinia);
app.use(notivue);
app.use(router);
app.use(VueKonva);

const authStore = useAuthStore();
// A provisioned terminal uses personal QR access, never a stored office identity.
if (router.resolve(window.location.pathname).meta.terminal !== true) {
  authStore.hydrate();
}
setupAxiosInterceptors(authStore, router);
const attendanceLock = useAttendanceLockStore();
attendanceLock.initSync();

const handleAuthNavigation = async (isAuthed: boolean) => {
  const currentRoute = router.currentRoute.value;
  if (
    isAuthed &&
    currentRoute.meta?.terminal !== true &&
    (currentRoute.meta?.public || currentRoute.name === 'login' || currentRoute.name === 'login-qr')
  ) {
    const redirect = typeof currentRoute.query.redirect === 'string' ? currentRoute.query.redirect : '';
    await router.replace(redirect.startsWith('/') ? redirect : await resolveAuthedHomeRouteWithCatalog(authStore));
    return;
  }

  if (!isAuthed && currentRoute.meta?.public !== true) {
    await router.replace({ name: 'login' });
  }
};

void router.isReady().then(() => {
  app.mount('#app');
  return handleAuthNavigation(authStore.isAuthenticated);
});
void attendanceLock.refresh();

authStore.$subscribe((_mutation, state) => {
  const isAuthed = Boolean(state.token && state.user);
  void handleAuthNavigation(isAuthed);
  void attendanceLock.refresh({ force: true });
});
